import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";
import { toast } from "sonner";
import { ChevronRight, ChevronDown, Download } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { KpiCard } from "@/components/kpi/KpiCard";
import { formatBs, formatUsd, formatCop } from "@/lib/format";

const PIE_COLORS = ["#1FB6A6", "#4AA3FF", "#F2B84B", "#2ED3A3", "#D98B5F", "#8B9BB0"];
// Fechas de calendario en hora Caracas (UTC-4), independientes de la zona del navegador.
const caracasNow = () => new Date(Date.now() - 4 * 3600 * 1000);
const isoOf = (d) => d.toISOString().slice(0, 10);
const todayISO = () => isoOf(caracasNow());
const money = (v, cur) => (cur === "USD" ? `$ ${formatUsd(v)}` : cur === "COP" ? `COP ${formatCop(v)}` : `Bs ${formatBs(v)}`);
const GAVETAS = [["bs", "BS", "Bs"], ["usd", "USD", "USD"], ["cop", "COP", "COP"]];

// Modalidades de juego solicitadas por QA (figuras/terminales/triples) → familia real
const MODALITIES = [
  { key: "animalitos", label: "Figuras" },
  { key: "chance", label: "Terminales" },
  { key: "tradicional", label: "Triples" },
];

// Semana anterior (lunes a domingo) respecto a hoy
function lastWeekRange() {
  const now = caracasNow();
  const dow = (now.getUTCDay() + 6) % 7; // 0=lunes
  const lastMonday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - dow - 7));
  const lastSunday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - dow - 1));
  return { date_from: isoOf(lastMonday), date_to: isoOf(lastSunday) };
}
// Mes anterior (día 1 a último día)
function lastMonthRange() {
  const now = caracasNow();
  const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const last = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  return { date_from: isoOf(first), date_to: isoOf(last) };
}

function ResumenTab() {
  const { data: summary } = useQuery({ queryKey: ["reports", "summary", 30], queryFn: async () => (await api.get("/reports/summary?days=30")).data });
  const { data: byDay = [] } = useQuery({ queryKey: ["reports", "by-day", 30], queryFn: async () => (await api.get("/reports/by-day?days=30")).data });
  const { data: byLottery = [] } = useQuery({ queryKey: ["reports", "by-lottery", 30], queryFn: async () => (await api.get("/reports/by-lottery?days=30")).data });
  const { data: byTaquilla = [] } = useQuery({ queryKey: ["reports", "by-taquilla", 30], queryFn: async () => (await api.get("/reports/by-taquilla?days=30")).data });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {GAVETAS.map(([k, cur, lbl]) => (
          <KpiCard key={k} label={`Venta Neta (${lbl})`} value={money(summary?.[k]?.venta_neta, cur)} testid={`resumen-kpi-${k}`}
            meta={`${summary?.[k]?.tickets ?? 0} tickets · Premios ${money(summary?.[k]?.payout, cur)} · Utilidad ${money(summary?.[k]?.profit, cur)}`}/>
        ))}
        <KpiCard label="Tickets válidos" value={summary?.tickets ?? 0} meta={`${summary?.winners ?? 0} ganadores`} accent="text-sky-300"/>
      </div>
      <p className="text-xs text-muted-foreground">Gráficos expresados en Bs: solo tickets emitidos en bolívares.</p>
      <Card className="p-4 bg-card border-border">
        <div className="font-display font-semibold mb-3">Ventas por día</div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={byDay}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false}/>
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false}/>
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }}/>
              <Line type="monotone" dataKey="total_bs" stroke="hsl(var(--primary))" strokeWidth={2} dot={false}/>
              <Line type="monotone" dataKey="payout_bs" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4 bg-card border-border">
          <div className="font-display font-semibold mb-3">Ventas por lotería</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={byLottery} dataKey="total_bs" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {byLottery.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="hsl(var(--card))"/>)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 12 }}/>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-4 bg-card border-border">
          <div className="font-display font-semibold mb-3">Top taquillas</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byTaquilla.slice(0, 8)} layout="vertical">
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" horizontal={false}/>
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false}/>
                <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} width={120} tickLine={false} axisLine={false}/>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }}/>
                <Bar dataKey="total_bs" fill="hsl(var(--chart-3))" radius={[4,4,4,4]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}

function DrillRow({ row, filters, cur }) {
  const [open, setOpen] = useState(false);
  const canDrill = filters.group_by === "entidad" && Boolean(row.key);
  const { data } = useQuery({
    queryKey: ["consolidated-drill", row.key, filters],
    queryFn: async () => (await api.get("/reports/consolidated", { params: { ...filters, group_by: "producto", agencia_id: row.key, hide_zeros: true } })).data,
    enabled: open && canDrill,
  });
  return (
    <>
      <tr className="border-t border-border/60 hover:bg-secondary/50" data-testid={`consolidated-row-${row.key || "none"}`}>
        <td className="px-3 py-2">
          <button className="inline-flex items-center gap-1.5 font-medium disabled:opacity-40" onClick={() => setOpen((o) => !o)} disabled={!canDrill} data-testid={`consolidated-expand-${row.key || "none"}`}>
            {canDrill ? (open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>) : <span className="w-3.5"/>}
            {row.label}{row.code ? <span className="text-muted-foreground font-mono text-xs ml-1">({row.code})</span> : null}
          </button>
          {row.sublabel && <div className="text-[11px] text-muted-foreground pl-5">{row.sublabel}</div>}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">{money(row.venta_bruta, cur)}</td>
        <td className="px-3 py-2 text-right tabular-nums text-rose-600 dark:text-rose-400">{money(row.venta_anulada, cur)}</td>
        <td className="px-3 py-2 text-right tabular-nums text-emerald-600 dark:text-emerald-300">{money(row.venta_neta, cur)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{money(row.com_agencia, cur)}</td>
        <td className="px-3 py-2 text-right tabular-nums text-amber-600 dark:text-amber-300">{money(row.premios_total, cur)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{money(row.subtotal, cur)}</td>
        <td className="px-3 py-2 text-right tabular-nums text-primary font-medium">{money(row.dif_comision, cur)}</td>
        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${row.total_cobrar >= 0 ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600 dark:text-rose-300"}`}>{money(row.total_cobrar, cur)}</td>
      </tr>
      {open && canDrill && (data?.rows || []).map((d) => (
        <tr key={d.key} className="bg-secondary/30 text-xs" data-testid={`consolidated-drill-${row.key}-${d.key}`}>
          <td className="pl-10 pr-3 py-1.5 text-muted-foreground">{d.label}</td>
          <td className="px-3 py-1.5 text-right tabular-nums">{money(d.venta_bruta, cur)}</td>
          <td className="px-3 py-1.5 text-right tabular-nums text-rose-600/80 dark:text-rose-400/80">{money(d.venta_anulada, cur)}</td>
          <td className="px-3 py-1.5 text-right tabular-nums text-emerald-600/80 dark:text-emerald-300/80">{money(d.venta_neta, cur)}</td>
          <td className="px-3 py-1.5 text-right tabular-nums">{money(d.com_agencia, cur)}</td>
          <td className="px-3 py-1.5 text-right tabular-nums text-amber-600/80 dark:text-amber-300/80">{money(d.premios_total, cur)}</td>
          <td className="px-3 py-1.5 text-right tabular-nums">{money(d.subtotal, cur)}</td>
          <td className="px-3 py-1.5 text-right tabular-nums text-primary/80">{money(d.dif_comision, cur)}</td>
          <td className={`px-3 py-1.5 text-right tabular-nums ${d.total_cobrar >= 0 ? "text-emerald-600/80 dark:text-emerald-300/80" : "text-rose-600/80 dark:text-rose-300/80"}`}>{money(d.total_cobrar, cur)}</td>
        </tr>
      ))}
    </>
  );
}

function ConsolidadasTab() {
  const [filters, setFilters] = useState({ date_from: todayISO(), date_to: todayISO(), currency: "BS", grupo_id: "", agencia_id: "", hide_zeros: true, group_by: "entidad", game_family: "" });
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const params = { ...filters, grupo_id: filters.grupo_id || undefined, agencia_id: filters.agencia_id || undefined, game_family: filters.game_family || undefined };

  const { data: grupos = [] } = useQuery({ queryKey: ["grupos"], queryFn: async () => (await api.get("/grupos")).data });
  const { data: agencias = [] } = useQuery({ queryKey: ["agencias"], queryFn: async () => (await api.get("/agencias")).data });
  const agenciasFiltered = filters.grupo_id ? agencias.filter((a) => a.grupo_id === filters.grupo_id) : agencias;

  const { data, isFetching } = useQuery({
    queryKey: ["consolidated", params],
    queryFn: async () => (await api.get("/reports/consolidated", { params })).data,
  });
  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const cur = filters.currency;

  const exportCsv = async () => {
    try {
      const res = await api.get("/reports/consolidated/export.csv", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `consolidado_${filters.date_from}_${filters.date_to}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch { toast.error("No se pudo exportar"); }
  };

  return (
    <div className="space-y-4" data-testid="consolidated-report">
      {/* Barra de filtros */}
      <Card className="p-3 bg-card border-border">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button onClick={() => set(lastWeekRange())} className="rounded-full border-2 border-border px-3 py-1.5 text-xs font-bold text-muted-foreground hover:bg-secondary/60" data-testid="consolidated-last-week">Semana Anterior</button>
          <button onClick={() => set(lastMonthRange())} className="rounded-full border-2 border-border px-3 py-1.5 text-xs font-bold text-muted-foreground hover:bg-secondary/60" data-testid="consolidated-last-month">Mes Anterior</button>
          <button onClick={() => set({ date_from: todayISO(), date_to: todayISO() })} className="rounded-full border-2 border-border px-3 py-1.5 text-xs font-bold text-muted-foreground hover:bg-secondary/60" data-testid="consolidated-today">Hoy</button>
          <div className="ml-auto inline-flex rounded-lg border-2 border-border overflow-hidden" data-testid="consolidated-scope-toggle">
            <button onClick={() => set({ group_by: "taquilla" })} className={`px-3 py-1.5 text-xs font-bold ${filters.group_by === "taquilla" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="consolidated-scope-taquilla">Taquilla</button>
            <button onClick={() => set({ group_by: "entidad" })} className={`px-3 py-1.5 text-xs font-bold ${filters.group_by === "entidad" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="consolidated-scope-agencia">Solo Agencia</button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8 gap-3 items-end">
          <div className="space-y-1 col-span-2"><Label className="text-xs">Rango de fechas</Label><DateRangePicker from={filters.date_from} to={filters.date_to} onChange={set} testid="consolidated-date-range"/></div>
          <div className="space-y-1">
            <Label className="text-xs">Moneda</Label>
            <Select value={filters.currency} onValueChange={(v) => set({ currency: v })}>
              <SelectTrigger data-testid="consolidated-currency"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="BS">Bs</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="COP">COP</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Grupo</Label>
            <Select value={filters.grupo_id || "all"} onValueChange={(v) => set({ grupo_id: v === "all" ? "" : v, agencia_id: "" })}>
              <SelectTrigger data-testid="consolidated-grupo"><SelectValue placeholder="Todos"/></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{grupos.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Agencia</Label>
            <Select value={filters.agencia_id || "all"} onValueChange={(v) => set({ agencia_id: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="consolidated-agencia"><SelectValue placeholder="Todas"/></SelectTrigger>
              <SelectContent><SelectItem value="all">Todas</SelectItem>{agenciasFiltered.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Agrupar por</Label>
            <Select value={filters.group_by} onValueChange={(v) => set({ group_by: v })}>
              <SelectTrigger data-testid="consolidated-groupby"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="entidad">Por Agencia</SelectItem><SelectItem value="taquilla">Por Taquilla</SelectItem><SelectItem value="producto">Por Producto</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Tipo de juego</Label>
            <Select value={filters.game_family || "all"} onValueChange={(v) => set({ game_family: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="consolidated-game-family"><SelectValue placeholder="Todos"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {MODALITIES.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-1">
            <Switch checked={filters.hide_zeros} onCheckedChange={(v) => set({ hide_zeros: v })} id="hide-zeros" data-testid="consolidated-hide-zeros"/>
            <Label htmlFor="hide-zeros" className="text-xs">Ocultar ceros</Label>
          </div>
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <KpiCard label={`Venta Neta (${cur})`} value={money(totals.venta_neta, cur)} accent="text-emerald-600 dark:text-emerald-300" testid="consolidated-kpi-neta"
          meta={`Bruta ${money(totals.venta_bruta, cur)} · Anulado ${money(totals.venta_anulada, cur)}`}/>
        <KpiCard label={`Total Comisión (${cur})`} value={money(totals.com_agencia, cur)} accent="text-sky-300"/>
        <KpiCard label={`Total Premios (${cur})`} value={money(totals.premios_total, cur)} accent="text-amber-600 dark:text-amber-300"/>
        <KpiCard label={`Total a Cobrar (${cur})`} value={money(totals.total_cobrar, cur)} accent="text-primary"/>
      </div>

      {/* Tabla analítica */}
      <Card className="bg-card border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="font-display font-semibold">Ventas Consolidadas {isFetching && <span className="text-xs text-muted-foreground ml-2">actualizando…</span>}</div>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="consolidated-export-csv"><Download size={14} className="mr-1.5"/>Exportar CSV (Excel)</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-muted-foreground text-left">
                <th className="px-3 py-2">{filters.group_by === "producto" ? "Producto" : filters.group_by === "taquilla" ? "Taquilla" : "Agencia"}</th>
                <th className="px-3 py-2 text-right">Venta Bruta</th>
                <th className="px-3 py-2 text-right">Anulaciones</th>
                <th className="px-3 py-2 text-right">Venta Neta</th>
                <th className="px-3 py-2 text-right">Com. Venta</th>
                <th className="px-3 py-2 text-right">Premios</th>
                <th className="px-3 py-2 text-right">Subtotal</th>
                <th className="px-3 py-2 text-right">Dif. Comisión</th>
                <th className="px-3 py-2 text-right">Total a Cobrar</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">Sin datos para el rango/filtros seleccionados.</td></tr>}
              {rows.map((r) => <DrillRow key={r.key || "none"} row={r} filters={params} cur={cur}/>)}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-border font-semibold" data-testid="consolidated-totals-row">
                  <td className="px-3 py-2">TOTAL</td>
                  <td className="px-3 py-2 text-right tabular-nums" data-testid="consolidated-total-bruta">{money(totals.venta_bruta, cur)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-rose-600 dark:text-rose-400" data-testid="consolidated-total-anulada">{money(totals.venta_anulada, cur)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-600 dark:text-emerald-300" data-testid="consolidated-total-neta">{money(totals.venta_neta, cur)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(totals.com_agencia, cur)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-600 dark:text-amber-300">{money(totals.premios_total, cur)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(totals.subtotal, cur)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-primary">{money(totals.dif_comision, cur)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${totals.total_cobrar >= 0 ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600 dark:text-rose-300"}`}>{money(totals.total_cobrar, cur)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
}

export default function ReportesPage() {
  return (
    <div className="space-y-4" data-testid="admin-table-reportes">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Reportes</h1>
        <p className="text-sm text-muted-foreground">Resumen operativo y ventas consolidadas por jerarquía comercial.</p>
      </div>
      <Tabs defaultValue="consolidadas">
        <TabsList>
          <TabsTrigger value="consolidadas" data-testid="reportes-tab-consolidadas">Ventas Consolidadas</TabsTrigger>
          <TabsTrigger value="resumen" data-testid="reportes-tab-resumen">Resumen 30 días</TabsTrigger>
        </TabsList>
        <TabsContent value="consolidadas" className="pt-3"><ConsolidadasTab/></TabsContent>
        <TabsContent value="resumen" className="pt-3"><ResumenTab/></TabsContent>
      </Tabs>
    </div>
  );
}
